import * as Sentry from "@sentry/react";
import axios, { AxiosError, AxiosRequestConfig } from "axios";

import {
  SellernoteUserAppName,
  SendRequestOptions,
  SendRequestOptionsData,
} from "@sellernote/shared/src/api/dto/entities/common/common";
import { MINUTE_AS_MILLISECONDS } from "@sellernote/shared/src/utils/date";
import {
  createReducerWithSagaBundle,
  CreateReducerWithSagaBundleProps,
} from "@sellernote/shared/src/utils/redux";

import { APP_NAME, IS_UNDER_LOCAL_DEVELOPMENT } from "../constants";
import {
  ShipdaCurrentLanguage,
  ShipdaCurrentLanguageType,
} from "../i18n/i18nForShipda";
import { API_URL_BY_APP, APP_BASE_URL_FOR_AUTH } from "./apiURL";

const API = axios.create({
  timeout: MINUTE_AS_MILLISECONDS,
  responseType: "json",
});

API.interceptors.response.use((response) => {
  // Any status code that lie within the range of 2xx cause this function to trigger
  // Do something with response data
  return response;
}, requestErrorHandler);

function requestErrorHandler(error: AxiosError<unknown>) {
  if (error.response?.status === 401) {
    return noAuthHandler(error);
  }

  if (checkNeedToErrorReport(error, error.response?.status)) {
    /**
     * axios 요청 관련 에러로도 sentry로 리포트 함
     * (Next.js에서도 잘 됨)
     *
     * API관련 오류는 백엔드에서 따로 모니터링하고 있으므로 참고용으로 전송
     */
    Sentry.captureException(error, {
      tags: {
        errorType: "API",
        level: "info",
      },
    });
  }

  return Promise.reject(error);
}

function checkNeedToErrorReport(
  error: AxiosError<unknown>,
  responseStatus?: number
): boolean {
  if (IS_UNDER_LOCAL_DEVELOPMENT) return false;

  /**
   * XHR요청이 취소 되는 경우
   * (ex. response 전에 페이지 이동 or request 취소)
   */
  if (axios.isCancel(error)) {
    return false;
  }

  /**
   * XHR요청이 abort되는 경우
   * - response 전에 페이지 이동 or request 취소
   * - timeout
   */
  // abort되는 경우를 모두 리포트 하지 않는건 과할 수 있어 보류함
  // if (error.code === "ECONNABORTED") {
  //   return false;
  // }

  if (responseStatus && responseStatus < 500) {
    return false;
  }

  return true;
}

async function noAuthHandler(error: AxiosError<unknown>) {
  const isAppNeedToRefreshToken = getIsAppNeedToRefreshToken();

  const newToken = isAppNeedToRefreshToken ? await getNewToken() : null;

  if (!newToken) {
    window.localStorage.removeItem("accessToken");
    window.localStorage.removeItem("refreshToken");

    window.sessionStorage.removeItem("accessToken");
    window.sessionStorage.removeItem("refreshToken");

    const backTo = window?.history?.state?.as;

    if (backTo) {
      window.location.href = `/login?backTo=${backTo}`;
    } else {
      window.location.href = "/login";
    }

    return Promise.reject(error);
  }

  const { config } = error;

  const failedRequestConfig: AxiosRequestConfig = {
    baseURL: config.baseURL,
    method: config.method,
    params: config.params,
    data: config.data,
    url: config.url,
    headers: {
      ...config.headers,
      authorization: `Bearer ${newToken}`,
    },
  };

  return API(failedRequestConfig);
}

async function getNewToken() {
  const isAutoLogin = window.localStorage.getItem("accessToken");

  const accessToken =
    window.localStorage.getItem("accessToken") ||
    window.sessionStorage.getItem("accessToken");
  const refreshToken =
    window.localStorage.getItem("refreshToken") ||
    window.sessionStorage.getItem("refreshToken");

  if (!accessToken || !refreshToken) {
    return null;
  }

  try {
    const { data } = await axios.post(`${APP_BASE_URL_FOR_AUTH}/auth/refresh`, {
      accessToken,
      refreshToken,
    });

    if (isAutoLogin) {
      window.localStorage.setItem("accessToken", data.accessToken);
    } else {
      window.sessionStorage.setItem("accessToken", data.accessToken);
    }

    // 초대내역 모달을 띄우기 위한 flag
    window.localStorage.setItem("needCheckTeamInvitation", "TRUE");

    return data.accessToken;
  } catch (e) {
    return null;
  }
}

async function sendRequest(options: SendRequestOptions) {
  const headers: {
    authorization?: string;
    pragma?: string;
    locale?: "KR";
    region?: "KR";
    lang?: ShipdaCurrentLanguageType;
    "Content-Type"?: string;
  } = {
    pragma: "no-cache",
    locale: options.locale || "KR",
    region: options.region || "KR",
    lang: options.lang || ShipdaCurrentLanguage.currentLanguage,
    ...(options.contentType
      ? {
          "Content-Type": options.contentType,
        }
      : {}),
    ...(options["X-API-KEY"]
      ? {
          "X-API-KEY": options["X-API-KEY"],
        }
      : {}),
  };

  const accessToken =
    typeof window !== "undefined" &&
    (window.localStorage.getItem("accessToken") ||
      window.sessionStorage.getItem("accessToken"));

  if (accessToken) {
    headers.authorization = `Bearer ${accessToken}`;
  }

  const config: AxiosRequestConfig = {
    method: options.method,
    params: options.params,
    /**
     * TODO: _customPath사용하는 곳 없어지면 삭제 (pathParams를 사용하는 방식으로 수정중)
     * request할때 _customPath를 보내면 그것을 사용
     */
    url: options.data?._customPath || options.path,
    data: getValidRequestData(options.data),
    headers,
    responseType: options.responseType ?? "json",
    signal: options.signal,
  };

  if (options.isLocalMiddleWare) {
    delete config?.headers?.authorization;
    delete config?.headers?.pragma;
    delete config?.headers?.locale;
  }

  // apiType을 특정하는 경우 baseURL을 바꿔준다
  if (options.apiType) {
    if (options.apiType === "custom") {
      config.baseURL = options.baseURL;
      config.headers = options.customHeader;
    } else {
      config.baseURL = (() => {
        const baseURL =
          API_URL_BY_APP[options.apiType]?.[APP_NAME as SellernoteUserAppName];
        if (!baseURL) {
          // 정상적인 flow라면 해당 App에서 제공하는 API만 사용하므로 baseURL은 반드시 있어야 한다.
          throw Error("Not found baseURL");
        }

        return baseURL;
      })();
    }
  }

  return API(config);
}

function getValidRequestData(requestData?: SendRequestOptionsData) {
  if (!requestData) return;

  // 실제 요청 payload와 관계없는 속성은 삭제
  delete requestData.pathParams;
  // TODO: _customPath사용하는 곳 없어지면 삭제 (pathParams를 사용하는 방식으로 수정중)
  delete requestData._customPath;

  return requestData;
}

function createReducerWithSagaBundleForBoful<
  KEY extends string,
  REQUEST_PAYLOAD,
  RESPONSE_PAYLOAD,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  SLICE_STATE extends { [P in string]: any },
>({
  sliceName,
  parentStateName,
  actionTypeKey,
  getRequestOptions,
  customActionBeforeSaga,
  customInitHandler,
  customSuccessHandler,
  customFailureHandler,
  loadingActions,
}: Omit<
  CreateReducerWithSagaBundleProps<
    KEY,
    REQUEST_PAYLOAD,
    RESPONSE_PAYLOAD,
    SLICE_STATE
  >,
  "sendRequestFunction"
>) {
  return createReducerWithSagaBundle<
    KEY,
    REQUEST_PAYLOAD,
    RESPONSE_PAYLOAD,
    SLICE_STATE
  >({
    sendRequestFunction: sendRequest,
    loadingActions,
    sliceName,
    parentStateName,
    actionTypeKey,
    getRequestOptions,
    customActionBeforeSaga,
    customInitHandler,
    customSuccessHandler,
    customFailureHandler,
  });
}

function createReducerWithSagaBundleForShipDa<
  KEY extends string,
  REQUEST_PAYLOAD,
  RESPONSE_PAYLOAD,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  SLICE_STATE extends { [P in string]: any },
>({
  sliceName,
  parentStateName,
  actionTypeKey,
  getRequestOptions,
  customActionBeforeSaga,
  customInitHandler,
  customSuccessHandler,
  customFailureHandler,
  loadingActions,
}: Omit<
  CreateReducerWithSagaBundleProps<
    KEY,
    REQUEST_PAYLOAD,
    RESPONSE_PAYLOAD,
    SLICE_STATE
  >,
  "sendRequestFunction"
>) {
  return createReducerWithSagaBundle<
    KEY,
    REQUEST_PAYLOAD,
    RESPONSE_PAYLOAD,
    SLICE_STATE
  >({
    sendRequestFunction: sendRequest,
    loadingActions,
    sliceName,
    parentStateName,
    actionTypeKey,
    getRequestOptions,
    customActionBeforeSaga,
    customInitHandler,
    customSuccessHandler,
    customFailureHandler,
  });
}

function createReducerWithSagaBundleForAdmin<
  KEY extends string,
  REQUEST_PAYLOAD,
  RESPONSE_PAYLOAD,
  SLICE_STATE extends { [P in string]: any },
>({
  sliceName,
  parentStateName,
  actionTypeKey,
  getRequestOptions,
  customActionBeforeSaga,
  customInitHandler,
  customSuccessHandler,
  customFailureHandler,
  loadingActions,
}: Omit<
  CreateReducerWithSagaBundleProps<
    KEY,
    REQUEST_PAYLOAD,
    RESPONSE_PAYLOAD,
    SLICE_STATE
  >,
  "sendRequestFunction"
>) {
  return createReducerWithSagaBundle<
    KEY,
    REQUEST_PAYLOAD,
    RESPONSE_PAYLOAD,
    SLICE_STATE
  >({
    sendRequestFunction: sendRequest,
    loadingActions,
    sliceName,
    parentStateName,
    actionTypeKey,
    getRequestOptions,
    customActionBeforeSaga,
    customInitHandler,
    customSuccessHandler,
    customFailureHandler,
  });
}

/**
 * 자동로그인이 되면 안되는 앱은 새 토큰을 받아서 재요청하는 처리를 하지 않는다.
 */
function getIsAppNeedToRefreshToken(): boolean {
  switch (APP_NAME) {
    case "partner-admin":
    case "boful-worker-web":
    case "boful-worker-pda": {
      return false;
    }
    case "shipda-kr": {
      return true;
    }

    default:
      return false;
  }
}

export {
  sendRequest,
  createReducerWithSagaBundleForAdmin,
  createReducerWithSagaBundleForShipDa,
  createReducerWithSagaBundleForBoful,
};
